import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MydataPageShell } from './MydataPageShell';
import {
  Callout,
  Card,
  LoadingSpinner,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type {
  MydataConnection,
  MydataPeriodSummary,
  MydataYearSummary,
} from '../../types/mydata';
import { formatDate, formatDateTime, formatMoney } from '../../utils/format';
import { MydataCharts } from './MydataCharts';
import { MydataYearFilterBar } from './MydataYearFilterBar';

const STALE_MS = 60 * 60 * 1000;

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${year}-${pad2(month)}-01`,
    to: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
}

function toNum(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function isStale(syncedAt: string | null | undefined): boolean {
  if (!syncedAt) return true;
  const t0 = new Date(syncedAt).getTime();
  if (Number.isNaN(t0)) return true;
  return Date.now() - t0 > STALE_MS;
}

function MetricCard({
  title,
  value,
  delta,
  href,
  emphasize,
}: {
  title: string;
  value: string;
  delta?: string | null;
  href?: string;
  emphasize?: boolean;
}) {
  const deltaNum = delta != null ? Number(delta) : null;
  const deltaLabel =
    deltaNum == null || Number.isNaN(deltaNum)
      ? null
      : `${deltaNum > 0 ? '+' : ''}${formatMoney(delta)}`;

  const card = (
    <Card
      className={[
        'h-full p-4 transition-shadow',
        href ? 'hover:border-brand/25 hover:shadow-bf-md' : '',
        emphasize
          ? 'border-brand/30 bg-brand-soft'
          : '',
      ].join(' ')}
    >
      <div className="text-xs font-medium uppercase tracking-wider text-ink-faint">
        {title}
      </div>
      <div
        className={[
          'mt-3 text-2xl font-semibold tabular-nums',
          emphasize ? 'text-brand' : 'text-ink',
        ].join(' ')}
      >
        {formatMoney(value)}
      </div>
      {deltaLabel ? (
        <div
          className={[
            'mt-1.5 text-xs tabular-nums',
            deltaNum != null && deltaNum > 0
              ? 'text-emerald-700 dark:text-emerald-400'
              : deltaNum != null && deltaNum < 0
                ? 'text-rose-700 dark:text-rose-400'
                : 'text-ink-muted',
          ].join(' ')}
        >
          {t().mydata.delta}: {deltaLabel}
        </div>
      ) : null}
    </Card>
  );

  if (!href) return card;
  return (
    <Link to={href} className="block">
      {card}
    </Link>
  );
}

export function MydataPeriodPage() {
  const labels = t().mydata;
  const monthNames = t().common.months;
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const initial = monthRange(now.getFullYear(), now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [summary, setSummary] = useState<MydataPeriodSummary | null>(null);
  const [yearSummary, setYearSummary] = useState<MydataYearSummary | null>(
    null,
  );
  const [connection, setConnection] = useState<MydataConnection | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [yearLoading, setYearLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  const applyMonth = useCallback((y: number, m: number) => {
    const range = monthRange(y, m);
    setYear(y);
    setMonth(m);
    setDateFrom(range.from);
    setDateTo(range.to);
  }, []);

  // Connection once
  useEffect(() => {
    let cancelled = false;
    void api
      .getMydataConnection()
      .then((conn) => {
        if (!cancelled) setConnection(conn);
      })
      .catch(() => {
        /* period/year load surfaces errors */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Year panel — only when year changes
  useEffect(() => {
    let cancelled = false;
    async function loadYear() {
      setYearLoading(true);
      try {
        const yearSum = await api.getMydataYearSummary({ year });
        if (!cancelled) setYearSummary(yearSum);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setYearLoading(false);
      }
    }
    void loadYear();
    return () => {
      cancelled = true;
    };
  }, [year]);

  // Period metrics — soft refresh, keep shell mounted
  useEffect(() => {
    let cancelled = false;
    async function loadPeriod() {
      if (hasLoadedOnce.current) setPeriodLoading(true);
      else setInitialLoading(true);
      setError(null);
      try {
        const sum = await api.getMydataSummary({ dateFrom, dateTo });
        if (cancelled) return;
        setSummary(sum);
        hasLoadedOnce.current = true;
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setPeriodLoading(false);
        }
      }
    }
    void loadPeriod();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const configured = connection?.configured ?? false;
  const stale = summary?.synced ? isStale(summary.syncedAt) : false;
  const previous = summary?.previous ?? null;
  const vatPayable = toNum(summary?.vatPayable);
  const yearVat = toNum(yearSummary?.vatPayable);

  const periodLabel = `${monthNames[month - 1] ?? ''} ${year}`;

  if (initialLoading && !summary) {
    return (
      <MydataPageShell>
        <LoadingSpinner message={t().common.loading} />
      </MydataPageShell>
    );
  }

  return (
    <MydataPageShell>
      <div className="shrink-0 space-y-1">
        <h1 className="text-lg font-semibold text-ink">{labels.dashboardTitle}</h1>
        <p className="text-sm text-ink-muted">{labels.periodHint}</p>
        <p className="text-xs text-ink-faint">
          {labels.lastSyncLabel}:{' '}
          {summary?.synced && summary.syncedAt
            ? formatDateTime(summary.syncedAt)
            : labels.neverSynced}
          {connection?.environment
            ? ` · ${
                connection.environment === 'production'
                  ? labels.production
                  : labels.sandbox
              }`
            : ''}
          {!configured ? ` · ${labels.connectionMissing}` : ''}
        </p>
      </div>

      <MydataYearFilterBar
        year={year}
        onYearChange={(y) => applyMonth(y, month)}
      />

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}
      {!configured ? (
        <Callout variant="warning" className="shrink-0">
          {labels.notConfigured}{' '}
          <Link to="/settings/mydata-connection" className="underline">
            {labels.connectionTitle}
          </Link>
        </Callout>
      ) : null}
      {stale && summary?.synced ? (
        <Callout variant="warning" className="shrink-0">
          {labels.staleHintSyncPage}{' '}
          <Link to="/mydata/sync" className="underline">
            {labels.goToSync}
          </Link>
        </Callout>
      ) : null}
      {!summary?.synced ? (
        <Callout variant="info" className="shrink-0">
          {labels.noSummary}{' '}
          <Link to="/mydata/sync" className="underline">
            {labels.goToSync}
          </Link>
        </Callout>
      ) : null}

      {/* Year overview first */}
      <Card
        className={[
          'shrink-0 p-5 transition-opacity',
          yearLoading ? 'opacity-60' : '',
        ].join(' ')}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              {labels.yearPanelTitle} {year}
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              {formatDate(`${year}-01-01`)} – {formatDate(`${year}-12-31`)}
              {' · '}
              {yearSummary?.source === 'full_year'
                ? labels.yearSourceFullYear
                : yearSummary?.source === 'months'
                  ? labels.yearSourceMonths
                  : yearSummary?.source === 'documents'
                    ? labels.yearSourceDocuments
                    : labels.yearSourceEmpty}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>
              {labels.yearMonthsSynced}:{' '}
              <strong className="tabular-nums text-ink">
                {yearSummary?.monthsSynced ?? 0}/12
              </strong>
            </span>
            <span>
              {labels.yearDocuments}:{' '}
              <strong className="tabular-nums text-ink">
                {yearSummary?.documentsTotal ?? 0}
              </strong>
            </span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="p-3 text-sm" variant="outlined">
            <div className="text-xs text-ink-faint">{labels.incomeNet}</div>
            <div className="mt-2 text-lg font-semibold tabular-nums">
              {formatMoney(yearSummary?.incomeNet ?? '0.00')}
            </div>
          </Card>
          <Card className="p-3 text-sm" variant="outlined">
            <div className="text-xs text-ink-faint">{labels.expenseNet}</div>
            <div className="mt-2 text-lg font-semibold tabular-nums">
              {formatMoney(yearSummary?.expenseNet ?? '0.00')}
            </div>
          </Card>
          <Card className="p-3 text-sm" variant="outlined">
            <div className="text-xs text-ink-faint">{labels.netResult}</div>
            <div className="mt-2 text-lg font-semibold tabular-nums">
              {formatMoney(yearSummary?.netResult ?? '0.00')}
            </div>
          </Card>
          <Card className="border-brand/30 bg-brand-soft p-3 text-sm">
            <div className="text-xs text-brand">
              {yearVat < 0 ? labels.vatRefundable : labels.vatPayable}
            </div>
            <div className="mt-2 text-lg font-semibold tabular-nums text-brand">
              {formatMoney(
                yearVat < 0
                  ? String(Math.abs(yearVat).toFixed(2))
                  : (yearSummary?.vatPayable ?? '0.00'),
              )}
            </div>
          </Card>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-1.5">
          {(
            yearSummary?.months ??
            Array.from({ length: 12 }, (_, i) => ({
              month: i + 1,
              synced: false,
              netResult: '0.00',
              incomeNet: '0.00',
            }))
          ).map((m) => {
            const selected = month === m.month;
            const shortName =
              (monthNames[m.month - 1] ?? '').slice(0, 3) || String(m.month);
            return (
              <button
                key={m.month}
                type="button"
                title={monthNames[m.month - 1]}
                onClick={() => applyMonth(year, m.month)}
                className={[
                  'min-w-0 rounded-lg border px-1.5 py-2 text-center transition-colors',
                  selected
                    ? 'border-brand bg-brand-soft'
                    : m.synced
                      ? 'border-line bg-white hover:border-brand/25 dark:bg-slate-900'
                      : 'border-line/60 bg-slate-50 text-ink-faint hover:border-line dark:bg-slate-900/40',
                ].join(' ')}
              >
                <div className="truncate text-[11px] font-medium text-ink-faint">
                  {shortName}
                </div>
                <div className="mt-1 truncate text-[11px] tabular-nums font-semibold text-ink">
                  {m.synced ? formatMoney(m.netResult) : '—'}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Selected period metrics — below year */}
      <div
        className={[
          'shrink-0 space-y-2 transition-opacity',
          periodLoading ? 'opacity-60' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-ink">
            {periodLabel}
            <span className="ml-2 font-normal text-ink-faint">
              {formatDate(dateFrom)} – {formatDate(dateTo)}
            </span>
            {periodLoading ? (
              <span className="ml-2 text-xs font-normal text-ink-faint">
                {t().common.loading}
              </span>
            ) : null}
          </h2>
          <Link
            to="/mydata/documents"
            className="text-sm text-brand hover:underline"
          >
            {labels.goToDocuments}
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            title={labels.incomeNet}
            value={summary?.incomeNet ?? '0.00'}
            delta={previous?.incomeNetDelta}
          />
          <MetricCard
            title={labels.expenseNet}
            value={summary?.expenseNet ?? '0.00'}
            delta={previous?.expenseNetDelta}
          />
          <MetricCard
            title={labels.netResult}
            value={summary?.netResult ?? '0.00'}
            delta={previous?.netResultDelta}
          />
          <MetricCard
            title={vatPayable < 0 ? labels.vatRefundable : labels.vatPayable}
            value={
              vatPayable < 0
                ? String(Math.abs(vatPayable).toFixed(2))
                : (summary?.vatPayable ?? '0.00')
            }
            delta={previous?.vatPayableDelta}
            href="/mydata/debts"
            emphasize
          />
        </div>
      </div>

      {/* Previous period + shortcuts */}
      <div
        className={[
          'grid shrink-0 gap-4 md:grid-cols-2 transition-opacity',
          periodLoading ? 'opacity-60' : '',
        ].join(' ')}
      >
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-ink">{labels.vsPrevious}</h2>
          {previous ? (
            <div className="mt-3 space-y-2 text-sm">
              <div className="text-ink-muted">
                {formatDate(previous.dateFrom)} – {formatDate(previous.dateTo)}
              </div>
              <div className="flex justify-between text-ink-muted">
                <span>{labels.incomeNet}</span>
                <span className="tabular-nums text-ink">
                  {formatMoney(previous.incomeNet)}
                </span>
              </div>
              <div className="flex justify-between text-ink-muted">
                <span>{labels.expenseNet}</span>
                <span className="tabular-nums text-ink">
                  {formatMoney(previous.expenseNet)}
                </span>
              </div>
              <div className="flex justify-between border-t border-line pt-2 font-semibold text-ink">
                <span>{labels.netResult}</span>
                <span className="tabular-nums">
                  {formatMoney(previous.netResult)}
                </span>
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-ink-faint">{labels.noPrevious}</p>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold text-ink">{labels.quickActions}</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {(
              [
                { to: '/mydata/sync', label: labels.goToSync },
                { to: '/mydata/documents', label: labels.goToDocuments },
                { to: '/mydata/debts', label: labels.goToDebts },
                { to: '/settings/mydata-connection', label: labels.goToConnection },
              ] as const
            ).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-lg border border-line px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-brand/25 hover:bg-brand-soft/40"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <div
        className={[
          'transition-opacity',
          periodLoading || yearLoading ? 'opacity-60' : '',
        ].join(' ')}
      >
        <MydataCharts summary={summary} yearSummary={yearSummary} />
      </div>
    </MydataPageShell>
  );
}
